import React, { createContext, useContext } from 'react';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

/**
 * Context for sharing Google Maps loading state across the application.
 * Part of Store Location Editor feature (STORE_LOCATION_EDITOR_ENTERPRISE_PLAN).
 */
const GoogleMapsContext = createContext(null);

/**
 * Provider component that loads Google Maps API once and shares state.
 * Wrap your app with this provider to enable Google Maps features.
 *
 * @example
 * // In App.js:
 * import { GoogleMapsProvider } from './shared/providers/GoogleMapsProvider';
 *
 * function App() {
 *   return (
 *     <GoogleMapsProvider>
 *       <YourApp />
 *     </GoogleMapsProvider>
 *   );
 * }
 *
 * // In any component:
 * import { useGoogleMapsContext } from './shared/providers/GoogleMapsProvider';
 *
 * function MyComponent() {
 *   const { isLoaded, loadError } = useGoogleMapsContext();
 *   // ...
 * }
 */
export const GoogleMapsProvider = ({ children }) => {
  const googleMapsState = useGoogleMaps();

  return (
    <GoogleMapsContext.Provider value={googleMapsState}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

/**
 * Hook to access Google Maps loading state from anywhere in the app.
 *
 * @returns {Object} { isLoaded: boolean, loadError: string|null }
 * @throws {Error} If used outside GoogleMapsProvider
 *
 * @example
 * const { isLoaded, loadError } = useGoogleMapsContext();
 *
 * if (loadError) return <div>Error loading maps</div>;
 * if (!isLoaded) return <div>Loading...</div>;
 * return <MapComponent />;
 */
export const useGoogleMapsContext = () => {
  const context = useContext(GoogleMapsContext);

  if (context === null) {
    throw new Error('useGoogleMapsContext must be used within GoogleMapsProvider');
  }

  return context;
};
